import { useEffect, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, BancontactStatus, ReceiptProductStatus } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatDate } from "../../lib/time";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { AdminCodeDialog } from "../orders/AdminCodeDialog";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

/**
 * Beheer › Algemeen › Bancontact Pro QR on the receipt (ADR 0029): the shop's product "On a Receipt", its
 * API key and its product id. The owner pastes both once; the key is shown to Bancontact, stored encrypted
 * and never given back. The address Bancontact may call back is always shown and never changes: the portal
 * of Bancontact only takes it while the product is being made, so the owner needs it before the product.
 * Saving or removing the product first asks the special admin code from Tafo: the receipts pay into its
 * account (security review, 30/10/2026).
 */
export function ReceiptQrCard({ authorization }: Props) {
  const { api, report, toast } = useTill();
  const text = t.general.qr.receipt;
  const [status, setStatus] = useState<ReceiptProductStatus | null>(null);
  const [key, setKey] = useState("");
  const [productId, setProductId] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [removing, setRemoving] = useState(false);
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null);
  /** What waits for the special admin code: a new product, or null to remove it. */
  const [waiting, setWaiting] = useState<{ product: { apiKey: string; productId: string } | null } | null>(null);

  useEffect(() => {
    let current = true;
    api
      .request<BancontactStatus>("GET", "/device/bancontact", { staffToken: authorization.token, retry: true })
      .then((answer) => {
        if (!current) return;
        setStatus(answer.receipt);
        setCallbackUrl(answer.receipt.callbackUrl ?? null);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, authorization.token, report]);

  const send = async (product: { apiKey: string; productId: string } | null, adminCode: string) => {
    setBusy(true);
    setMessage("");
    try {
      const answer = await api.request<ReceiptProductStatus>("PUT", "/device/bancontact-receipt", {
        staffToken: authorization.token,
        body: { product },
        headers: { "x-admin-code": adminCode },
      });
      setStatus(answer);
      // a server from before the address became fixed leaves it out: keep what is shown
      if (answer.callbackUrl !== undefined) setCallbackUrl(answer.callbackUrl);
      // the key leaves the screen the moment it is stored
      setKey("");
      setProductId("");
      toast(product === null ? text.removed : text.saved);
    } catch (error) {
      if (error instanceof ApiError && ([400, 409, 502].includes(error.status) || error.code.includes("admin_code"))) {
        setMessage(error.message);
      } else {
        report(error);
      }
    } finally {
      setBusy(false);
    }
  };

  if (status === null) return null;
  const complete = key.trim() !== "" && productId.trim() !== "";
  return (
    <div className="gen-bancontact receiptqr">
      <h4>{text.title}</h4>
      {status.active && status.setAt !== null && (
        <p className="gen-bc-on">
          <b>{t.general.qr.bancontact.on(formatDate(status.setAt), status.setBy ?? "")}</b>
        </p>
      )}
      {callbackUrl !== null && (
        <div className="hookurl">
          <p className="note">{text.callback}</p>
          <div className="inl">
            <input
              readOnly
              value={callbackUrl}
              aria-label={text.callbackLabel}
              onFocus={(event) => event.target.select()}
            />
            <button
              type="button"
              className="btn"
              onClick={() => {
                void navigator.clipboard
                  .writeText(callbackUrl)
                  .then(() => {
                    toast(text.copied);
                  })
                  .catch(() => undefined);
              }}
            >
              {text.copy}
            </button>
          </div>
        </div>
      )}
      <form
        className="inl gen-inl gen-bc-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (complete && !busy) setWaiting({ product: { apiKey: key, productId } });
        }}
      >
        <label>
          {text.productId}{" "}
          <input
            type="text"
            value={productId}
            placeholder={text.productPlaceholder}
            autoComplete="off"
            spellCheck={false}
            maxLength={100}
            onChange={(event) => {
              setProductId(event.target.value);
              setMessage("");
            }}
          />
        </label>
        <label>
          {t.general.qr.bancontact.key}{" "}
          <input
            type="password"
            value={key}
            placeholder={t.general.qr.bancontact.placeholder}
            autoComplete="off"
            spellCheck={false}
            maxLength={5000}
            onChange={(event) => {
              setKey(event.target.value);
              setMessage("");
            }}
          />
        </label>
        <button type="submit" className="btn primary" disabled={busy || !complete}>
          {status.active ? text.replace : text.save}
        </button>
        {status.active && (
          <button
            type="button"
            className="btn danger"
            disabled={busy}
            onClick={() => {
              setRemoving(true);
            }}
          >
            {text.remove}
          </button>
        )}
      </form>
      {busy && <p className="note">{t.general.qr.bancontact.checking}</p>}
      {message !== "" && (
        <p className="pinmsg" role="alert">
          {message}
        </p>
      )}
      {status.active && <p className="note">{text.hidden}</p>}
      {removing && (
        <ConfirmDialog
          message={text.removeConfirm}
          confirm={text.remove}
          onConfirm={() => {
            setWaiting({ product: null });
          }}
          onClose={() => {
            setRemoving(false);
          }}
        />
      )}
      {waiting !== null && (
        <AdminCodeDialog
          beheer={authorization}
          note={t.general.qr.bancontact.adminCode}
          onUnlocked={(code) => {
            setWaiting(null);
            void send(waiting.product, code);
          }}
          onClose={() => {
            setWaiting(null);
          }}
        />
      )}
    </div>
  );
}
